import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FiRefreshCw, FiCheckCircle, FiAlertTriangle, FiMapPin, FiBriefcase, FiList, FiPlus, FiX } from 'react-icons/fi';
import { atualizarClientePelaReceita } from '../../services/clientes';
import { listarPerfisNf, criarPerfilNf, excluirPerfilNf } from '../../services/nfPerfis';
import { buscarCep } from '../../services/cep';
import SeletorMunicipio from '../../componentes/SeletorMunicipio';
import styles from './Clientes.module.css';

/* Endereço completo, dados do cartão CNPJ e CNAEs.
   Os CNAEs são o motivo principal desta tela existir: é deles que saem os
   códigos de serviço (CTN/NBS) que vão na nota fiscal, e até agora não
   ficavam registrados em lugar nenhum.

   Tudo é preenchido pelo botão "Atualizar pela Receita", mas segue
   editável -- a base da Receita às vezes vem incompleta (logradouro vazio
   é comum) e travar o cadastro por isso seria pior. */

function dataBr(iso) {
  if (!iso) return '';
  const [a, m, d] = String(iso).slice(0, 10).split('-');
  return `${d}/${m}/${a}`;
}

function Campo({ rotulo, valor, onChange, largo, tipo = 'text', somenteLeitura, dica }) {
  return (
    <div className={`${styles.dadosCampo} ${largo ? styles.dadosCampoLargo : ''}`}>
      <span className={styles.dadosLabel}>{rotulo}</span>
      {somenteLeitura ? (
        <span className={styles.dadosValor}>{valor || '—'}</span>
      ) : (
        <input className={styles.dadosInput} type={tipo} value={valor ?? ''}
          onChange={e => onChange(e.target.value)} />
      )}
      {dica && <span style={{ fontSize: 11, color: '#6B8CAE', marginTop: 3 }}>{dica}</span>}
    </div>
  );
}

/* mesmos códigos da Nibo (businessActivity). Decide se a Inscrição
   Estadual entra na base do Licenciamento; vazio = deduz pelo CNAE. */
export const TIPOS_ATIVIDADE = [
  { v: 2, l: 'Serviço' }, { v: 1, l: 'Comércio' }, { v: 4, l: 'Indústria' },
  { v: 3, l: 'Comércio e Serviço' }, { v: 5, l: 'Comércio e Indústria' },
  { v: 6, l: 'Serviço e Indústria' }, { v: 7, l: 'Comércio, Serviço e Indústria' },
];

/* enquadramento = o "Regime" da aba Vencimentos (nesta ordem) */
export const ENQUADRAMENTOS = [
  { id: 'mei',       label: 'MEI' },
  { id: 'simples',   label: 'Simples Nacional' },
  { id: 'presumido', label: 'Lucro Presumido' },
  { id: 'real',      label: 'Lucro Real' },
  { id: 'sem_fins',  label: 'Sem fins lucrativos' },
];
export const rotuloEnquadramento = (id) => ENQUADRAMENTOS.find(x => x.id === id)?.label || '';

const soDig = (v) => String(v || '').replace(/\D/g, '');
/* 8111700 -> 8111-7/00 */
const cnaeFormatado = (c) => { const d = soDig(c); return d.length === 7 ? `${d.slice(0, 4)}-${d[4]}/${d.slice(5)}` : c; };

/* marcação NFS no início do CNAE: ligada = existe perfil de tributação da
   NF pra ele (criado aqui, a equipe completa os códigos depois) */
function FlagNfs({ ativo, ocupado, onAlternar }) {
  return (
    <button type="button" onClick={onAlternar} disabled={ocupado}
      title={ativo ? 'Tem perfil de NFS para este CNAE (clique para excluir o perfil)' : 'Criar perfil de NFS para este CNAE'}
      style={{
        flexShrink: 0, minWidth: 40, padding: '2px 7px', borderRadius: 6, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em',
        cursor: ocupado ? 'wait' : 'pointer', border: `1px solid ${ativo ? '#2f6db5' : '#cbd8e6'}`,
        background: ativo ? '#2f6db5' : '#fff', color: ativo ? '#fff' : '#8BA5C5',
      }}>
      NFS
    </button>
  );
}

function DadosReceita({ cliente, editando, dados, set, onAtualizado }) {
  const [perfis, setPerfis] = useState([]);
  const [ocupado, setOcupado] = useState('');
  const [erroPerfil, setErroPerfil] = useState('');
  const carregarPerfis = useCallback(() => {
    if (!cliente?.id) return Promise.resolve();
    return listarPerfisNf(cliente.id).then(r => setPerfis(r.items || [])).catch(() => {});
  }, [cliente?.id]);
  useEffect(() => { carregarPerfis(); }, [carregarPerfis]);
  const perfilDoCnae = (codigo) => perfis.find(p => soDig(p.cnae) && soDig(p.cnae) === soDig(codigo));

  async function alternarNfs(codigo, descricao) {
    if (!soDig(codigo)) return;
    setErroPerfil('');
    const p = perfilDoCnae(codigo);
    if (p && !window.confirm(`Excluir o perfil de NFS "${p.nome}"? Os códigos preenchidos nele se perdem.`)) return;
    setOcupado(codigo);
    try {
      if (p) await excluirPerfilNf(cliente.id, p.id);
      else await criarPerfilNf(cliente.id, { nome: `${cnaeFormatado(codigo)} - ${descricao || ''}`.trim().slice(0, 150), cnae: codigo });
      await carregarPerfis();
    } catch (e) { setErroPerfil(e.message); }
    finally { setOcupado(''); }
  }

  const [buscando, setBuscando] = useState(false);
  const [msg, setMsg] = useState('');
  const [erro, setErro] = useState('');
  const [cepEstado, setCepEstado] = useState(''); // '', 'buscando', 'ok' ou a mensagem de erro
  const ultimoCep = useRef('');

  /* em modo de edição usa o rascunho; fora dele mostra o que está salvo */
  const v = (campo) => (editando ? dados[campo] : cliente[campo]);

  const cnaes = (editando ? dados.cnaesSecundarios : cliente.cnaesSecundarios) || [];

  async function atualizar() {
    setBuscando(true); setMsg(''); setErro('');
    try {
      const r = await atualizarClientePelaReceita(cliente.id);
      onAtualizado(r.cliente);
      setMsg(`Cadastro atualizado pela Receita${r.cnaesSecundarios ? ` · ${r.cnaesSecundarios} CNAE(s) secundário(s)` : ''}.`);
    } catch (e) {
      setErro(e.message);
    } finally {
      setBuscando(false);
    }
  }

  /* CEP completo busca o endereço nos Correios na hora -- é de onde sai o
     logradouro que a Receita quase sempre deixa em branco. Só preenche
     campo que veio com valor: consulta incompleta não apaga o que já
     estava digitado. Número e complemento nunca vêm do CEP. */
  async function cepDigitado(valor) {
    const digitos = String(valor).replace(/\D/g, '').slice(0, 8);
    set('cep', digitos);
    if (digitos.length < 8) {
      ultimoCep.current = '';
      setCepEstado('');
      return;
    }
    if (digitos === ultimoCep.current) return; // não repete a consulta do mesmo CEP
    ultimoCep.current = digitos;
    setCepEstado('buscando');
    try {
      const e = await buscarCep(digitos);
      if (e.logradouro)      set('logradouro', e.logradouro);
      if (e.bairro)          set('bairro', e.bairro);
      /* município e UF o backend preenche a partir do código do IBGE, pra
         nome e código nunca divergirem */
      if (e.codigoMunicipio) set('codigoMunicipio', e.codigoMunicipio);
      setCepEstado('ok');
    } catch (err) {
      setCepEstado(err.message);
    }
  }

  return (
    <>
      <div className={styles.dadosSecao}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
          <p className={styles.dadosSecaoTitulo} style={{ margin: 0 }}>
            <FiBriefcase size={12} style={{ marginRight: 5 }} /> Dados da Receita
          </p>
          {cliente.cnpj ? (
            <button type="button" className={styles.drawerBtnAcao} onClick={atualizar} disabled={buscando}>
              <FiRefreshCw size={13} className={buscando ? styles.girando : ''} />
              {buscando ? 'Consultando...' : 'Atualizar pela Receita'}
            </button>
          ) : (
            <span style={{ fontSize: 11.5, color: '#b45309' }}>
              Sem CNPJ cadastrado — a consulta à Receita não se aplica.
            </span>
          )}
        </div>

        {msg && (
          <p className={styles.dadosAviso} style={{ color: '#15803d', marginTop: 8 }}>
            <FiCheckCircle size={12} style={{ marginRight: 4 }} />{msg}
          </p>
        )}
        {erro && (
          <p className={styles.dadosAviso} style={{ color: '#b91c1c', marginTop: 8 }}>
            <FiAlertTriangle size={12} style={{ marginRight: 4 }} />{erro}
          </p>
        )}
        {cliente.receitaAtualizadaEm && !msg && (
          <p className={styles.dadosAviso} style={{ marginTop: 8 }}>
            Última atualização pela Receita: {new Date(cliente.receitaAtualizadaEm).toLocaleString('pt-BR')}
          </p>
        )}

        <div className={styles.dadosGrid} style={{ marginTop: 12 }}>
          <Campo rotulo="Nome fantasia" valor={v('nomeFantasia')}
            onChange={x => set('nomeFantasia', x)} somenteLeitura={!editando} />
          <Campo rotulo="Natureza jurídica" valor={v('naturezaJuridica')}
            onChange={x => set('naturezaJuridica', x)} somenteLeitura={!editando} />
          <Campo rotulo="Porte" valor={v('porte')}
            onChange={x => set('porte', x)} somenteLeitura={!editando} />
          <Campo rotulo="Situação cadastral" valor={v('situacaoCadastral')}
            onChange={x => set('situacaoCadastral', x)} somenteLeitura={!editando} />
          <Campo rotulo="Data de abertura" valor={editando ? v('dataAbertura') : dataBr(v('dataAbertura'))}
            onChange={x => set('dataAbertura', x)} tipo="date" somenteLeitura={!editando} />
          <Campo rotulo="Capital social (R$)" valor={v('capitalSocial')}
            onChange={x => set('capitalSocial', x)} tipo="number" somenteLeitura={!editando} />
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Tipo de atividade</span>
            {editando ? (
              <select className={styles.dadosInput} value={dados.tipoAtividade ?? ''}
                onChange={e => set('tipoAtividade', e.target.value)}>
                <option value="">Pelo CNAE (automático)</option>
                {TIPOS_ATIVIDADE.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            ) : (
              <span className={styles.dadosValor}>
                {TIPOS_ATIVIDADE.find(t => t.v === cliente.tipoAtividade)?.l || 'Pelo CNAE'}
              </span>
            )}
          </div>
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Enquadramento</span>
            {editando ? (
              /* MEI e Simples acertam as opções do Simples no backend */
              <select className={styles.dadosInput} value={dados.enquadramento || ''}
                onChange={e => set('enquadramento', e.target.value)}>
                <option value="">Não definido</option>
                {ENQUADRAMENTOS.map(x => <option key={x.id} value={x.id}>{x.label}</option>)}
              </select>
            ) : (
              <span className={styles.dadosValor} style={!cliente.enquadramento ? { color: '#b45309' } : undefined}>
                {rotuloEnquadramento(cliente.enquadramento) || 'Não definido'}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.dadosSecao}>
        <p className={styles.dadosSecaoTitulo}>
          <FiMapPin size={12} style={{ marginRight: 5 }} /> Endereço
        </p>
        <div className={styles.dadosGrid}>
          {editando ? (
            <Campo
              rotulo="CEP"
              valor={v('cep')}
              onChange={cepDigitado}
              dica={cepEstado === 'buscando' ? 'Buscando endereço…'
                : cepEstado === 'ok' ? 'Endereço preenchido pelos Correios.'
                : cepEstado || 'Digite os 8 dígitos para preencher o endereço.'}
            />
          ) : (
            <Campo rotulo="CEP" valor={v('cep')} somenteLeitura />
          )}
          <Campo rotulo="Logradouro" valor={v('logradouro')} onChange={x => set('logradouro', x)}
            largo somenteLeitura={!editando} />
          <Campo rotulo="Número" valor={v('numero')} onChange={x => set('numero', x)} somenteLeitura={!editando} />
          <Campo rotulo="Complemento" valor={v('complemento')} onChange={x => set('complemento', x)} somenteLeitura={!editando} />
          <Campo rotulo="Bairro" valor={v('bairro')} onChange={x => set('bairro', x)} somenteLeitura={!editando} />
          <div className={styles.dadosCampo}>
            <span className={styles.dadosLabel}>Município</span>
            {editando ? (
              <SeletorMunicipio className={styles.dadosInput} valor={dados.codigoMunicipio}
                onChange={x => set('codigoMunicipio', x)} />
            ) : (
              <span className={styles.dadosValor}>
                {cliente.municipio ? `${cliente.municipio}/${cliente.uf}` : '—'}
                {cliente.codigoMunicipio && (
                  <span style={{ color: '#93a1b3', marginLeft: 6, fontSize: 11.5 }}>
                    IBGE {cliente.codigoMunicipio}
                  </span>
                )}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.dadosSecao}>
        <p className={styles.dadosSecaoTitulo}>
          <FiList size={12} style={{ marginRight: 5 }} /> Atividades (CNAE)
        </p>
        <p className={styles.dadosAviso} style={{ marginBottom: 10 }}>
          Define os códigos de serviço que vão na nota fiscal. Puxe pela Receita
          para trazer o principal e todos os secundários, ou clique em Editar
          para informar à mão (CNPJ recém-aberto ainda não aparece na Receita).
        </p>

        {editando ? (
          <>
            <div className={styles.cnaeEdicaoLinha}>
              <input className={styles.dadosInput} style={{ width: 110, flex: 'none' }} placeholder="Código"
                value={dados.cnaePrincipal} onChange={e => set('cnaePrincipal', e.target.value)} />
              <input className={styles.dadosInput} placeholder="Descrição do CNAE principal"
                value={dados.cnaePrincipalDescricao} onChange={e => set('cnaePrincipalDescricao', e.target.value)} />
              <span className={styles.cnaeEtiqueta}>principal</span>
            </div>
            {cnaes.map((c, i) => (
              <div key={i} className={styles.cnaeEdicaoLinha}>
                <input className={styles.dadosInput} style={{ width: 110, flex: 'none' }} placeholder="Código"
                  value={c.codigo} onChange={e => set('cnaesSecundarios', cnaes.map((x, j) => j === i ? { ...x, codigo: e.target.value } : x))} />
                <input className={styles.dadosInput} placeholder="Descrição do CNAE secundário"
                  value={c.descricao} onChange={e => set('cnaesSecundarios', cnaes.map((x, j) => j === i ? { ...x, descricao: e.target.value } : x))} />
                <button type="button" className={styles.cnaeRemover} title="Remover"
                  onClick={() => set('cnaesSecundarios', cnaes.filter((_, j) => j !== i))}>
                  <FiX size={13} />
                </button>
              </div>
            ))}
            <button type="button" className={styles.drawerBtnAcao} style={{ marginTop: 6 }}
              onClick={() => set('cnaesSecundarios', [...cnaes, { codigo: '', descricao: '' }])}>
              <FiPlus size={13} /> Adicionar CNAE secundário
            </button>
          </>
        ) : (
        <>
        {erroPerfil && <p className={styles.dadosAviso} style={{ color: '#b91c1c' }}>{erroPerfil}</p>}
        <div className={styles.cnaePrincipal}>
          {cliente.cnaePrincipal && (
            <FlagNfs ativo={!!perfilDoCnae(cliente.cnaePrincipal)} ocupado={ocupado === cliente.cnaePrincipal}
              onAlternar={() => alternarNfs(cliente.cnaePrincipal, cliente.cnaePrincipalDescricao)} />
          )}
          <span className={styles.cnaeCodigo}>{cliente.cnaePrincipal || '—'}</span>
          <span className={styles.cnaeDescricao}>
            {cliente.cnaePrincipalDescricao || 'CNAE principal não informado'}
          </span>
          <span className={styles.cnaeEtiqueta}>principal</span>
        </div>

        {cnaes.length > 0 ? (
          <div className={styles.cnaeLista}>
            {cnaes.map(c => (
              <div key={c.codigo} className={styles.cnaeItem}>
                <FlagNfs ativo={!!perfilDoCnae(c.codigo)} ocupado={ocupado === c.codigo}
                  onAlternar={() => alternarNfs(c.codigo, c.descricao)} />
                <span className={styles.cnaeCodigo}>{c.codigo}</span>
                <span className={styles.cnaeDescricao}>{c.descricao}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className={styles.dadosAviso} style={{ marginTop: 8 }}>
            Nenhum CNAE secundário registrado.
          </p>
        )}
        </>
        )}
      </div>
    </>
  );
}

export default DadosReceita;
